import { SyntheticEvent } from 'react'
import { TokenStatsProps, TokensState } from '../../../types/mfc'
import { IShareThing } from '../../components/presentation/Modals/Drawers/ShareThing'
import { TipProps } from '../../components/presentation/Modals/Drawers/Tip'
import type { YourNewsSection } from '../../components/presentation/News/drawers/YourNews'
import { ModelPoolDrawerProps } from '../../components/presentation/YouMayLike/page/ModelPoolDrawer'
import {
    ABOUT,
    ACCOUNT,
    AUTHENTICATION,
    CHAT_COMMANDS,
    CHAT_OPTIONS,
    FIND_PINNED_ROOMS,
    FRIEND_CONFIRMATION,
    HIDDEN_MODELS,
    IGNORED_MODELS,
    LOUNGES,
    LOUNGE_AUTOPLAY_OPTIONS,
    MODELS_YOU_MAY_LIKE_POOL,
    MODEL_NOTIFICATIONS,
    MY_ACCOUNT,
    NEWS_YOUR_NEWS,
    NOTES,
    PARTICIPANTS,
    PROFILE,
    REPORT_USER,
    SEND_MAIL_MESSAGE,
    SETTINGS_HOME,
    SHARE_THING,
    SIGNUP,
    SUPPORT,
    TIPS,
    TIPS_SHARE,
    TOKENS,
    TOKEN_STATS,
    TOPIC,
    USER_FEEDBACK,
    WHITEBOARD,
} from '../../constants/drawer'
import {
    ConfirmationType,
    DrawerHeight,
    ModalKind,
} from '../../constants/enums'
import { locales } from '../../locales/en'
import { getLens } from '../../mfcLogic/lens'
import { safeObj } from '../../utilities/safe'
import { getModalState } from '../getState'
import { setModal, setShowModal } from '../internal/modals'
import { openCloseDraftConfirmationDialog } from './state.dialog'

type DrawerProps = {
    onBack?(): void
    stickyHeader?: boolean
    title?: string
}

export const closeDrawer = (immediate = false) => {
    // a drawer holding unsaved work confirms before it is dismissed
    if (getModalState().drawer?.guardClose && !immediate) {
        openCloseDraftConfirmationDialog(() => closeDrawer(true))
        return
    }
    // safeTransition(() => {
    setShowModal(ModalKind.Drawer, false, immediate)
    // }, skipTransition)
    // setShowModal(ModalKind.Drawer, false, immediate)
}

export const openAboutDrawer = (props: DrawerProps = {}) => {
    const { onBack } = props
    setModal(ModalKind.Drawer, {
        type: ABOUT,
        title: 'About MyFreeCams',
        stickyHeader: true,
        show: true,
        onBack,
    })
}

export const openAccountDrawer = () =>
    setModal(ModalKind.Drawer, { type: ACCOUNT, title: '\u200b', show: true })

export const AUTH_SUBJECTS = {
    ADD_FRIEND: 'add a friend',
    ADD_NOTE: 'add a note',
    BOOKMARK_MODEL: 'bookmark a model',
    HIDE_MODEL: 'hide a model',
    IGNORE_USER: 'ignore a user',
    REPORT_USER: 'report a user',
    SEND_PM: 'send a PM',
    SEND_QUICK_REPLY: 'send a quick reply',
    START_OR_JOIN_SHOW: 'start or join a show',
    VIEW_CHAT_ARCHIVES: 'view chat archives',
    VIEW_PRIVATE_GALLERY: 'view your private gallery',
} as const

export type AuthSubject = (typeof AUTH_SUBJECTS)[keyof typeof AUTH_SUBJECTS]

type AuthenticationProps = {
    showPromoBanner?: boolean
    subject?: AuthSubject
    redirect?: string
} & DrawerProps

export const openAuthenticationDrawer = ({
    onBack,
    showPromoBanner,
    subject,
    redirect = null,
}: AuthenticationProps = {}) =>
    setModal(ModalKind.Drawer, {
        type: AUTHENTICATION,
        title: 'LOG IN',
        stickyHeader: true,
        show: true,
        showPromoBanner,
        subject,
        redirect,
        onBack,
    })

export const openAutoplayOptionsDrawer = () =>
    setModal(ModalKind.Drawer, {
        type: LOUNGE_AUTOPLAY_OPTIONS,
        title: 'Autoplay Options',
        stickyHeader: true,
        drawerHeight: DrawerHeight.Limited,
        show: true,
    })

export const openChatOptionsDrawer = ({
    pinned = false,
    lounge = false,
    isOverlay = false,
} = {}) =>
    setModal(ModalKind.Drawer, {
        type: CHAT_OPTIONS,
        title: 'Chat Options',
        stickyHeader: true,
        drawerHeight: DrawerHeight.Limited,
        show: true,
        pinned,
        lounge,
        isOverlay,
    })

export const openChatCommandsDrawer = (modelId?: number) =>
    setModal(ModalKind.Drawer, {
        type: CHAT_COMMANDS,
        title: 'Chat Commands',
        stickyHeader: true,
        drawerHeight: DrawerHeight.Limited,
        show: true,
        id: modelId,
    })

export const openContactSupportDrawer = (
    props?: SyntheticEvent | DrawerProps,
) => {
    const { onBack } = safeObj(props as DrawerProps)
    setModal(ModalKind.Drawer, {
        type: SUPPORT,
        title: 'CONTACT SUPPORT',
        stickyHeader: true,
        show: true,
        onBack,
    })
}

export const openFindPinnedRooms = () =>
    setModal(ModalKind.Drawer, {
        type: FIND_PINNED_ROOMS,
        title: 'Find a Room to Pin',
        show: true,
    })

export const openFriendConfirmationDrawer = (
    slug: string,
    confirmationType: ConfirmationType = ConfirmationType.Friend,
) =>
    setModal(ModalKind.Drawer, {
        type: FRIEND_CONFIRMATION,
        title: 'Are you sure?',
        confirmationType,
        slug,
        show: true,
    })

export const openHiddenModelsDrawer = (props: DrawerProps = {}) => {
    const { onBack } = props
    setModal(ModalKind.Drawer, {
        type: HIDDEN_MODELS,
        title: 'Hidden Models',
        stickyHeader: true,
        onBack,
        show: true,
    })
}

export const openIgnoredModelsDrawer = (props: DrawerProps = {}) => {
    const { onBack } = props
    setModal(ModalKind.Drawer, {
        type: IGNORED_MODELS,
        title: 'Ignored Models',
        stickyHeader: true,
        onBack,
        show: true,
    })
}

export const openLoungesDrawer = () => {
    getLens().Lounge_Click({ openDrawer: true })
    setModal(ModalKind.Drawer, { type: LOUNGES, show: true })
}

export const openModelNotificationsDrawer = (name: string) => {
    setModal(ModalKind.Drawer, {
        type: MODEL_NOTIFICATIONS,
        title: 'Model Notifications',
        stickyHeader: true,
        name,
        show: true,
    })
}

export const openModelsYouMayLikePoolDrawer = (props: ModelPoolDrawerProps) => {
    const { fromSettings = false, slug } = props
    getLens()?.Recommend_Click({ modelsMayLikeDrawer: true })
    setModal(ModalKind.Drawer, {
        type: MODELS_YOU_MAY_LIKE_POOL,
        title: fromSettings ? 'Models You May Like' : 'Advanced Selection',
        stickyHeader: true,
        drawerHeight: DrawerHeight.Limited,
        slug,
        show: true,
        fromSettings,
        ...(fromSettings ? { onBack: () => openSettingsDrawer() } : {}),
    })
}

type MyAccountProps = { section?: string } & DrawerProps

export const openMyAccountDrawer = (props: MyAccountProps = {}) => {
    const { onBack, section, title } = props
    setModal(ModalKind.Drawer, {
        type: MY_ACCOUNT,
        title: title ?? 'My Account',
        stickyHeader: true,
        section,
        onBack,
        show: true,
    })
}

// settingsOnly shows just the notification settings (as in My Account); the
// lists and online toggle only make sense when opened from the news page
export const openYourNewsDrawer = ({
    section,
    settingsOnly = false,
}: {
    section?: YourNewsSection
    settingsOnly?: boolean
} = {}) => {
    setModal(ModalKind.Drawer, {
        type: NEWS_YOUR_NEWS,
        title: settingsOnly
            ? locales.news.newsSettings.title
            : locales.news.yourNews.title,
        stickyHeader: true,
        listType: section,
        settingsOnly,
        show: true,
    })
}

export const openNotesDrawer = (slug: string) => {
    getLens()?.Notes_Click({ drawer: true })
    setModal(ModalKind.Drawer, {
        type: NOTES,
        title: 'Notes',
        slug,
        show: true,
    })
}

export const openParticipantsDrawer = (id: number) =>
    setModal(ModalKind.Drawer, {
        type: PARTICIPANTS,
        title: 'Participants',
        stickyHeader: true,
        id,
        show: true,
    })

export const openProfileDrawer = (username: string) => {
    getLens()?.Profile_Click({ drawer: true })
    setModal(ModalKind.Drawer, {
        type: PROFILE,
        title: `${username}'s Profile`,
        stickyHeader: true,
        drawerHeight: DrawerHeight.Half,
        drawer: true,
        slugOrId: username,
        show: true,
    })
}

export const openReportUserDrawer = ({
    id,
    name,
}: {
    id: number
    name: string
}) => {
    setModal(ModalKind.Drawer, {
        type: REPORT_USER,
        title: 'Report a User',
        stickyHeader: true,
        id,
        name,
        show: true,
    })
}

export const openSendMailDrawer = (to?: string) => {
    setModal(ModalKind.Drawer, {
        type: SEND_MAIL_MESSAGE,
        title: locales.mail.compose.title,
        to,
        show: true,
    })
}

export const setDrawerGuardClose = (guardClose: boolean) => {
    const drawer = getModalState().drawer
    if (!drawer?.show || !!drawer.guardClose === guardClose) return
    setModal(ModalKind.Drawer, { ...drawer, guardClose })
}

export const openSettingsDrawer = ({ onBack }: DrawerProps = {}) => {
    getLens()?.Settings_Click()
    setModal(ModalKind.Drawer, {
        type: SETTINGS_HOME,
        title: locales.settings.drawerTitle,
        stickyHeader: true,
        drawerHeight: DrawerHeight.Full,
        onBack,
        show: true,
    })
}

export const openShareThingDrawer = (props: IShareThing) => {
    const { name } = props
    setModal(ModalKind.Drawer, {
        ...props,
        type: SHARE_THING,
        title: `${name} on MFC Share`,
        stickyHeader: true,
        drawerHeight: DrawerHeight.Limited,
        show: true,
    })
}

interface ShareTipDrawerProps extends DrawerProps {
    name: string
}

export const openShareTipsDrawer = (props: ShareTipDrawerProps) => {
    const { name, onBack } = props
    const title = (name ? `${name}'s ` : '') + 'Tip Menu'
    getLens()?.TipMenu_Click()
    setModal(ModalKind.Drawer, {
        type: TIPS_SHARE,
        name,
        title,
        stickyHeader: true,
        onBack,
        show: true,
    })
}

type SignupDrawerProps = DrawerProps & {
    fromChat?: boolean
    showPromoBanner?: boolean
}

export const openSignupDrawer = (props?: SignupDrawerProps) => {
    const { onBack, fromChat, showPromoBanner } = safeObj(props)
    getLens()?.Signup_Click({ fromChat })

    setModal(ModalKind.Drawer, {
        type: SIGNUP,
        title: 'SIGN UP FOR MYFREECAMS',
        stickyHeader: true,
        showPromoBanner,
        onBack,
        show: true,
    })
}

export const openTipsDrawer = (props: TipProps) => {
    getLens()?.Tip_Click()
    setModal(ModalKind.Drawer, {
        ...props,
        type: TIPS,
        title: 'TIP',
        stickyHeader: true,
        show: true,
    })
}

type TokensDrawerProps = DrawerProps & TokensState

export const openTokensDrawer = (props?: TokensDrawerProps) => {
    getLens()?.Purchase_Click()
    const { onBack, queue, product, processor } = safeObj(props)
    setModal(ModalKind.Drawer, {
        type: TOKENS,
        title: 'Purchase Tokens',
        stickyHeader: true,
        onBack,
        queue,
        product,
        processor,
        show: true,
    })
}

type TokenStatsDrawerProps = TokenStatsProps & DrawerProps

export const openTokenStatsDrawer = (props?: TokenStatsDrawerProps) => {
    const { onBack, modelList, statsData, title, tokenUsageHistory } =
        safeObj(props)
    setModal(ModalKind.Drawer, {
        type: TOKEN_STATS,
        title: title ?? 'Token Stats',
        stickyHeader: true,
        onBack,
        modelList,
        statsData,
        tokenUsageHistory,
        show: true,
    })
}

export const openTopicDrawer = (slug: string) =>
    setModal(ModalKind.Drawer, {
        type: TOPIC,
        slug,
        title: false,
        show: true,
    })

export const openUserFeedbackDrawer = (props: DrawerProps = {}) => {
    const { onBack } = props
    setModal(ModalKind.Drawer, {
        type: USER_FEEDBACK,
        title: 'USER FEEDBACK',
        stickyHeader: true,
        onBack,
        show: true,
    })
}

export const openWhiteboardDrawer = (name: string) => {
    getLens()?.Whiteboard_Click({
        drawer: true,
    })
    setModal(ModalKind.Drawer, {
        type: WHITEBOARD,
        title: `${name}'s Whiteboard`,
        stickyHeader: true,
        identifier: name,
        hideTitle: true,
        showAvatar: false,
        show: true,
    })
}
